import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Store, MessageSquare, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import api from "../../lib/api";
import { Label } from "../../components/common";

function KpiScore({ value }) {
  if (value == null) return <span className="text-slate-400">–</span>;
  const tone = value >= 80 ? "text-emerald-700" : value >= 60 ? "text-amber-700" : "text-rose-700";
  return (
    <span className={`font-head text-4xl font-bold ${tone}`} data-testid="kpi-overall">
      {value}
      <span className="ml-1 text-base font-semibold text-slate-400">/100</span>
    </span>
  );
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function StoreKPIDetail() {
  const { locId } = useParams();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [detail, setDetail] = useState(null);
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    setDetail(null); setFeed(null);
    api.get(`/store-kpi/locations/${locId}?days=${days}`).then((r) => setDetail(r.data)).catch(() => setDetail({ error: true }));
    api.get(`/store-kpi/locations/${locId}/feedback?days=${days}&limit=100`).then((r) => setFeed(r.data)).catch(() => setFeed({ feedback: [] }));
  }, [locId, days]);

  if (!detail) return <div className="text-slate-400">Loading…</div>;
  if (detail.error) return <div className="text-rose-600">Could not load Store KPI drill-down.</div>;

  return (
    <div className="space-y-6" data-testid="store-kpi-detail">
      <button
        onClick={() => navigate("/app/store-kpi")}
        data-testid="back-to-ranking"
        className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
      >
        <ArrowLeft className="h-4 w-4" /> Back to ranking
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label>Store KPI · drill-down</Label>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">{detail.location_name}</h1>
          {detail.city && <p className="mt-1 text-sm text-slate-500">{detail.city}</p>}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/app/scorecards/outlet?location_id=${detail.location_id}&days=${days}`)}
            data-testid="open-outlet-scorecard"
            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800">
            Open full scorecard (PDF) →
          </button>
          <div className="flex gap-1 rounded-full border border-slate-200 bg-white p-1 text-xs" data-testid="kpi-days">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`kpi-days-${d}`}
                className={`rounded-full px-3 py-1 font-semibold transition-colors ${
                  days === d ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100"
                }`}>Last {d}d</button>
            ))}
          </div>
        </div>
      </div>

      {/* Overall + sample */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <Store className="h-4 w-4 text-amber-600" /> Overall Store KPI
          </div>
          <div className="mt-2"><KpiScore value={detail.overall} /></div>
          <p className="mt-2 text-xs text-slate-500">Average of all store-scope criteria normalised to 0–100.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sample size</div>
          <div className="mt-2 font-head text-4xl font-bold text-slate-900" data-testid="kpi-sample">{detail.sample}</div>
          <p className="mt-2 text-xs text-slate-500">Store-scope signals aggregated over the window.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Window</div>
          <div className="mt-2 font-head text-4xl font-bold text-slate-900">{detail.days}d</div>
          <p className="mt-2 text-xs text-slate-500">
            Trend bucketed by {detail.trend_bucket_days === 7 ? "week" : "day"}.
          </p>
        </div>
      </div>

      {/* Trend */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="kpi-trend">
        <div className="mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-indigo-500" />
          <h2 className="font-head text-base font-bold text-slate-900">Trend</h2>
        </div>
        {(detail.trend || []).length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-400">No trend data yet.</div>
        ) : (
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={detail.trend} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <Tooltip formatter={(v, n) => n === "overall" ? [`${v}/100`, "KPI"] : [v, n]} />
                <Line type="monotone" dataKey="overall" stroke="#B45309" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Per-criterion breakdown */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="kpi-dimensions">
          <h2 className="font-head text-base font-bold text-slate-900">Store dimensions</h2>
          <p className="mt-1 text-xs text-slate-500">Average 1–5 score and sample count.</p>
          <div className="mt-4 divide-y divide-slate-100">
            {(detail.dimensions || []).length === 0 && (
              <div className="py-4 text-sm text-slate-400">No store-scope dimensions configured.</div>
            )}
            {(detail.dimensions || []).map((d) => (
              <div key={d.key} className="flex items-center justify-between py-3" data-testid={`dim-${d.key}`}>
                <div>
                  <div className="font-semibold text-slate-900">{d.label}</div>
                  <div className="text-xs text-slate-500">{d.count} rating{d.count === 1 ? "" : "s"}</div>
                </div>
                <div className="font-head text-xl font-bold text-slate-900">
                  {d.avg == null ? <span className="text-slate-400">–</span> : d.avg.toFixed(2)}
                  {d.avg != null && <span className="text-xs text-slate-400"> / 5</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="kpi-questions">
          <h2 className="font-head text-base font-bold text-slate-900">Store yes/no questions</h2>
          <p className="mt-1 text-xs text-slate-500">% answered Yes, with sample count.</p>
          <div className="mt-4 divide-y divide-slate-100">
            {(detail.questions || []).length === 0 && (
              <div className="py-4 text-sm text-slate-400">No store-scope questions configured.</div>
            )}
            {(detail.questions || []).map((q) => (
              <div key={q.text} className="flex items-center justify-between py-3">
                <div className="pr-2">
                  <div className="font-semibold text-slate-900">{q.text}</div>
                  <div className="text-xs text-slate-500">{q.count} answer{q.count === 1 ? "" : "s"}</div>
                </div>
                <div className="font-head text-xl font-bold text-slate-900">
                  {q.yes_pct == null ? <span className="text-slate-400">–</span> : `${q.yes_pct}%`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Feedback behind the score */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="kpi-feedback-list">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4">
          <MessageSquare className="h-4 w-4 text-indigo-500" />
          <h2 className="font-head text-base font-bold text-slate-900">Feedback behind the score</h2>
          <span className="ml-auto text-xs text-slate-500">{feed?.count ?? "…"} with store signals</span>
        </div>
        {!feed ? (
          <div className="p-6 text-sm text-slate-400">Loading…</div>
        ) : (feed.feedback || []).length === 0 ? (
          <div className="p-6 text-sm text-slate-400">No store-scope feedback in this window.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {feed.feedback.map((f) => (
              <div key={f.id} className="px-5 py-4" data-testid={`fb-${f.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-head text-sm font-bold text-amber-700">{f.rating}★</span>
                    <span className="text-xs text-slate-400">{fmtDate(f.created_at)}</span>
                  </div>
                  <span className="font-mono text-[10px] text-slate-400">{f.id}</span>
                </div>
                {f.comment && <p className="mt-1 text-sm text-slate-700">{f.comment}</p>}
                <div className="mt-2 flex flex-wrap gap-2">
                  {(f.store_dimensions || []).map((d, i) => (
                    <span key={d.key || i} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                      <span className="font-semibold">{d.label || d.key}</span><span>{d.value}/5</span>
                    </span>
                  ))}
                  {(f.store_question_answers || []).map((qa, i) => (
                    <span key={i} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                      qa.value ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                    }`}>
                      <span className="font-semibold">{qa.q}</span><span>{qa.value ? "Yes" : "No"}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
